import { ROLE_LABEL } from "@/components/boo/access-copy";
import { BooMark } from "@/components/boo/BooMark";
import { RoleMenuItem, RolePermissionsPanel } from "@/components/OrgRoleParts";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, ApiError, type BooAccessGrant, type BooRole, type ContainerRow, type OrgRole } from "@/lib/api";
import { ASSIGNABLE_ORG_ROLES, ORG_ROLE_LABEL } from "@/lib/org-roles";
import { booRoleOfLevel, type DashboardLevel, dashboardLevels, levelLabel, levelOfRole } from "@/lib/people-levels";
import { cn } from "@/lib/utils";
import { LevelRadioGroup } from "@/pages/container-tabs/people/PersonParts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type BooChoice = "none" | BooRole;

const CHOICE_LABEL: Record<BooChoice, string> = { none: "No access", ...ROLE_LABEL };

export interface InvitePreset {
  email?: string;
  name?: string;
  // Set when the invite starts inside a Boo: the dialog hands out one level
  // on that Boo and nothing else. Without it (the org Admins page) it asks
  // for an org role, Admin by default, and optionally a level on each Boo the
  // inviter manages, No access by default.
  boo?: BooAccessGrant;
}

// The Admins page's title follows the org role picked.
const INVITE_TITLE: Partial<Record<OrgRole, string>> = {
  admin: "Invite admin",
  billing_admin: "Invite billing admin",
  member: "Invite read-only person",
};

const RANK: Record<BooRole, number> = { member: 0, admin: 1, owner: 2 };

async function grantAtLeast(userId: string, grant: BooAccessGrant): Promise<boolean> {
  const members = await api.containers.members.list(grant.containerId);
  const current = members.data.find((m) => m.userId === userId)?.role;
  if (current && RANK[current] >= RANK[grant.role]) return false;
  if (current) {
    await api.containers.members.setRole(grant.containerId, userId, grant.role);
    return true;
  }
  try {
    await api.containers.members.add(grant.containerId, userId, grant.role);
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.code === "already_a_member") return false;
    throw err;
  }
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteDialog({
  open,
  onClose,
  organizationId,
  preset,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  preset?: InvitePreset;
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="grid-cols-[minmax(0,1fr)] sm:max-w-lg">
        {open && <InviteForm onClose={onClose} organizationId={organizationId} preset={preset} />}
      </DialogContent>
    </Dialog>
  );
}

function manageable(boos: ContainerRow[]): ContainerRow[] {
  return boos.filter((b) => b._permissions?.canManageMembers === true);
}

function InviteForm({
  onClose,
  organizationId,
  preset,
}: {
  onClose: () => void;
  organizationId: string;
  preset?: InvitePreset;
}) {
  const queryClient = useQueryClient();
  const org = useQuery({
    queryKey: ["org-full", organizationId],
    queryFn: () => api.org.details(),
  });
  const boosQuery = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const scoped = preset?.boo ?? null;
  const scopedBoo = scoped ? boosQuery.data?.data.find((b) => b.id === scoped.containerId) : undefined;
  const scopedPerms = scopedBoo?._permissions ?? undefined;
  const boos = scoped ? [] : manageable(boosQuery.data?.data ?? []);
  const booName = scopedBoo?.name ?? "this Boo";

  const [email, setEmail] = useState(preset?.email ?? "");
  const [touched, setTouched] = useState(false);
  const [orgRole, setOrgRole] = useState<OrgRole>(scoped ? "member" : "admin");
  const [moreOpen, setMoreOpen] = useState(false);
  const [picked, setPicked] = useState<Record<string, BooChoice>>({});
  const [level, setLevel] = useState<DashboardLevel>(scoped ? levelOfRole(scoped.role) : "standard");
  const levels = dashboardLevels(scopedPerms);
  // Until the Boo's permissions arrive, or when they stop short of the
  // preset, the level falls back to the one anyone who can invite may give.
  const chosenLevel: DashboardLevel = levels.some((o) => o.key === level) ? level : "standard";
  const choiceFor = (boo: ContainerRow): BooChoice => picked[boo.id] ?? "none";

  const members = org.data?.data.members ?? [];
  const invitations = org.data?.data.invitations ?? [];
  const normalized = email.trim().toLowerCase();
  const existing = members.find((m) => m.user.email.toLowerCase() === normalized);
  const pending = invitations.some((i) => i.status === "pending" && i.email.toLowerCase() === normalized);
  const emailValid = EMAIL.test(normalized);
  const orgName = org.data?.data.name ?? "your organization";

  const grants: BooAccessGrant[] = scoped
    ? [{ containerId: scoped.containerId, role: booRoleOfLevel(chosenLevel) }]
    : boos.flatMap((b) => {
        const c = choiceFor(b);
        return c === "none" ? [] : [{ containerId: b.id, role: c }];
      });
  // Admin or Owner on a Boo asks for an org role too; Standard does not.
  const needsOrgRole = grants.some((g) => g.role !== "member");

  const send = useMutation({
    mutationFn: async () => {
      if (pending) throw new Error("An invitation is already waiting for this email. You can resend it from Admins.");
      if (existing) {
        if (grants.length === 0) throw new Error("Pick at least one Boo for them to open.");
        let changed = false;
        for (const g of grants) changed = (await grantAtLeast(existing.user.id, g)) || changed;
        return changed ? ("added" as const) : ("unchanged" as const);
      }
      await api.org.invite({ email: email.trim(), role: orgRole, boos: grants });
      return "invited" as const;
    },
    onSuccess: (outcome) => {
      queryClient.invalidateQueries({ queryKey: ["org-full", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["boo-people"] });
      queryClient.invalidateQueries({ queryKey: ["boo-members"] });
      const who = existing?.user.name || email.trim();
      toast.success(
        outcome === "added"
          ? scoped
            ? `${who} is now ${levelLabel(chosenLevel)} in ${booName}.`
            : `${who} can now open the Boos you picked.`
          : outcome === "unchanged"
            ? `${who} already had that access or more.`
            : `Invite sent to ${email.trim()}.`,
      );
      onClose();
    },
  });

  const emailProblem = touched && !emailValid ? "Enter an email address, like name@example.com." : null;

  return (
    <form
      className="flex min-w-0 flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (emailValid) send.mutate();
      }}
    >
      <DialogHeader>
        <DialogTitle className="text-balance">
          {preset?.name
            ? `Invite ${preset.name} to the dashboard`
            : scoped
              ? "Invite to the dashboard"
              : existing
                ? `Give ${existing.user.name || existing.user.email} Boo access`
                : (INVITE_TITLE[orgRole] ?? "Invite admin")}
        </DialogTitle>
        <DialogDescription className="sr-only">
          They&apos;ll get an email to join {orgName}
          {scoped ? ` and open ${booName}` : ""}.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="invite-email">Email</Label>
        <Input
          id="invite-email"
          type="email"
          autoFocus={!preset?.email}
          placeholder="name@example.com"
          value={email}
          aria-invalid={emailProblem ? true : undefined}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(email.trim() !== "")}
        />
        {emailProblem ? (
          <p className="text-xs text-destructive">{emailProblem}</p>
        ) : existing ? (
          <p className="text-xs text-muted-foreground">
            {existing.user.name || existing.user.email} is already in {orgName}.{" "}
            {scoped ? `They'll be added to ${booName} at the level you pick.` : "They'll get access to the Boos you pick below."}
          </p>
        ) : null}
      </div>

      {scoped ? (
        <>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium">Level in {booName}</legend>
            {boosQuery.isLoading ? (
              <div className="h-12 animate-pulse rounded-lg bg-muted" />
            ) : (
              <LevelRadioGroup value={chosenLevel} options={levels} onChange={setLevel} />
            )}
            {scopedBoo && !scopedPerms?.canManageAdmins && (
              <p className="text-xs text-muted-foreground">Only this Boo&apos;s owners can invite someone as Admin or Owner.</p>
            )}
          </fieldset>
          {!existing && needsOrgRole && (
            <div className="flex flex-col gap-2 rounded-lg bg-muted/50 p-3">
              <Label>Role in {orgName}</Label>
              <OrgRolePicker value={orgRole} onChange={setOrgRole} />
            </div>
          )}
        </>
      ) : (
        <>
          {!existing && (
            <div className="flex flex-col gap-2">
              <Label>Role in {orgName}</Label>
              <OrgRolePicker value={orgRole} onChange={setOrgRole} />
              <RolePermissionsPanel role={orgRole} />
            </div>
          )}
          <Collapsible open={moreOpen || !!existing} onOpenChange={setMoreOpen}>
            {!existing && (
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="-ml-1 inline-flex items-center gap-1 rounded px-1 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronRight className={cn("size-4 transition-transform", moreOpen && "rotate-90")} />
                  Also give access to Boos (optional)
                </button>
              </CollapsibleTrigger>
            )}
            <CollapsibleContent>
              <div className={cn("flex flex-col gap-2", !existing && "pt-3")}>
                {existing && <span className="text-sm font-medium">Boo access</span>}
                {boosQuery.isLoading ? (
                  <div className="h-12 animate-pulse rounded-lg bg-muted" />
                ) : boos.length === 0 ? (
                  <p className="rounded-lg bg-muted/50 px-3 py-2.5 text-sm text-muted-foreground">
                    You don&apos;t manage any Boos, so they&apos;ll join without access to one. A Boo&apos;s owner can add
                    them later.
                  </p>
                ) : (
                  <ul className="flex max-h-64 flex-col divide-y overflow-y-auto rounded-lg border">
                    {boos.map((boo) => {
                      const choice = choiceFor(boo);
                      const canAdmin = boo._permissions?.canManageAdmins === true;
                      return (
                        <li key={boo.id} className="flex items-center gap-3 px-3 py-2">
                          <BooMark id={boo.id} className="size-5 shrink-0" muted={choice === "none"} />
                          <span className={cn("min-w-0 flex-1 truncate text-sm", choice === "none" && "text-muted-foreground")}>
                            {boo.name}
                          </span>
                          <Select value={choice} onValueChange={(v) => setPicked((p) => ({ ...p, [boo.id]: v as BooChoice }))}>
                            <SelectTrigger className="h-8 w-[8.75rem] shrink-0" aria-label={`Access to ${boo.name}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent align="end">
                              <SelectItem value="none">{CHOICE_LABEL.none}</SelectItem>
                              <SelectItem value="member">{CHOICE_LABEL.member}</SelectItem>
                              {canAdmin && <SelectItem value="admin">{CHOICE_LABEL.admin}</SelectItem>}
                              {canAdmin && <SelectItem value="owner">{CHOICE_LABEL.owner}</SelectItem>}
                            </SelectContent>
                          </Select>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}

      {send.isError && <InlineError error={send.error} />}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" loading={send.isPending}>
          {existing ? "Give access" : "Send invite"}
        </Button>
      </div>
    </form>
  );
}

function OrgRolePicker({ value, onChange }: { value: OrgRole; onChange: (r: OrgRole) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between bg-background font-normal">
          {ORG_ROLE_LABEL[value]}
          <ChevronDown className="size-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] max-w-[20rem]">
        {ASSIGNABLE_ORG_ROLES.map((r) => (
          <RoleMenuItem key={r} role={r} selected={value === r} onSelect={() => onChange(r)} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
